import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Bell, FileCheck, Wallet, Plus, MapPin, Smartphone, CalendarDays, ChevronLeft, AlertTriangle, FileText, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { getLocation, SERVICE, fmtDate } from "@/lib/util";
import { isIOS, isStandalone } from "@/lib/push";
import { KuwaitPlate } from "@/components/KuwaitPlate";
import { tr as t, Auto } from "@/lib/i18n";

const StatBox = ({ to, bg, icon: I, ic, label, value, unit, testid }) => (
  <Link to={to} data-testid={testid} className={`rounded-2xl p-3 ${bg} flex flex-col justify-between min-h-[100px] shadow-sm active:scale-[0.97] transition-transform`}>
    <div className="flex items-start justify-between gap-1"><span className="text-xs text-slate-600 font-bold">{label}</span><I className={`w-6 h-6 shrink-0 ${ic}`} /></div>
    <div className="flex items-end justify-between"><span className="text-xl font-black text-slate-900 whitespace-nowrap">{value}{unit && <small className="text-xs font-bold"> {unit}</small>}</span><ChevronLeft className="w-4 h-4 text-slate-400 ltr:rotate-180" /></div>
  </Link>
);

const Tile = ({ to, icon: I, bg, title, sub, badge, testid }) => (
  <Link to={to} data-testid={testid} className="relative bg-white rounded-2xl shadow-[0_4px_18px_rgba(1,59,121,0.08)] p-3 flex flex-col items-center text-center gap-2 active:scale-[0.96] transition-transform">
    <span className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-md ${bg}`}><I className="w-7 h-7" /></span>
    {badge > 0 && <span data-testid={`${testid}-badge`} className="absolute top-1.5 left-2 min-w-6 h-6 px-1 rounded-full bg-red-600 text-white text-xs font-black flex items-center justify-center border-2 border-white">{badge}</span>}
    <b className="text-[15px] text-slate-900 leading-tight">{title}</b>
    <span className="text-[10px] text-slate-500 leading-tight">{sub}</span>
  </Link>
);

function LocationNotice() {
  const [show, setShow] = useState(!localStorage.getItem("loc_notice_ok"));
  if (!show) return null;
  const ok = async () => {
    localStorage.setItem("loc_notice_ok", "1");
    setShow(false);
    await getLocation();
  };
  const [a, b] = t("يتم تسجيل موقعك {b} (إنشاء نقلة، تغيير الوجهة، رفع الصور، التسليم). لا يوجد تتبع مستمر.").split("{b}");
  return (
    <div className="card p-4 border-brand/20 bg-brand-soft fade-up" data-testid="location-notice">
      <div className="flex gap-3">
        <MapPin className="w-7 h-7 text-brand shrink-0" />
        <p className="text-brand-dark text-sm">{a}<b>{t("فقط عند تنفيذ إجراءات العمل")}</b>{b}</p>
      </div>
      <button data-testid="location-notice-ok" onClick={ok} className="btn btn-md btn-blue w-full mt-3">{t("فهمت، اسمح بالموقع")}</button>
    </div>
  );
}

function LatestAlerts() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/my/notifications").then((r) => setList(r.data.slice(0, 3))).catch(() => setList([])); }, []);
  if (!list || !list.length) return null;
  return (
    <div className="bg-white rounded-2xl shadow-[0_4px_18px_rgba(1,59,121,0.08)] px-4 py-3" data-testid="driver-latest-alerts">
      <div className="flex items-center justify-between mb-1"><b className="text-base text-slate-900">{t("آخر التنبيهات")}</b>
        <Link to="/driver/notifications" data-testid="driver-alerts-all" className="text-sm text-brand font-bold flex items-center gap-1">{t("عرض الكل")}<ChevronLeft className="w-4 h-4 ltr:rotate-180" /></Link></div>
      {list.map((n) => {
        const vio = n.type === "violation";
        return (
          <Link key={n.rid} to={vio && n.violation_id ? `/driver/violations/${n.violation_id}` : "/driver/notifications"} className="flex items-center gap-3 py-3 border-t border-slate-100 first:border-0">
            <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${vio ? "bg-red-50 text-red-600" : "bg-brand-soft text-brand"}`}>{vio ? <AlertTriangle className="w-5 h-5" /> : <FileText className="w-5 h-5" />}</span>
            <div className="flex-1 min-w-0"><b className="text-sm block text-slate-900 truncate"><Auto text={n.title} /></b><span className="text-xs text-slate-500 block truncate"><Auto text={n.body} /></span></div>
            <div className="flex flex-col items-end gap-1 text-[10px] text-slate-400 shrink-0"><span className={`w-2 h-2 rounded-full ${n.read_at ? "bg-slate-300" : "bg-red-500"}`} />{fmtDate(n.created_at)}</div>
          </Link>
        );
      })}
    </div>
  );
}

function Hero({ d, unread, logout }) {
  const v = d.vehicle;
  return (
    <div className="relative bg-brand text-white pb-14 safe-top overflow-hidden">
      <div className="absolute inset-0 h-[320px] bg-gradient-to-b from-brand-light to-brand" />
      <div className="absolute -top-20 -left-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
      <div className="relative px-4 pt-4">
        <div className="flex items-center justify-between">
          <img src="/img/logo.png" alt={t("شعار الشركة")} data-testid="company-logo" className="w-14 h-14 rounded-full bg-white p-0.5 object-contain shadow-lg ring-2 ring-white/60" />
          <div className="text-center"><div className="text-lg font-black">{t("المحيط الأطلسي")}</div><div className="text-[11px] text-white/70">{t("لنقل البضائع والمعدات")}</div></div>
          <div className="flex gap-2">
            <Link to="/driver/notifications" data-testid="hero-bell" className="relative w-11 h-11 rounded-full bg-white/10 backdrop-blur border border-white/15 flex items-center justify-center"><Bell className="w-5 h-5" />{unread > 0 && <span className="absolute top-2 right-2.5 w-2.5 h-2.5 rounded-full bg-red-500" />}</Link>
            <button onClick={logout} data-testid="driver-logout-btn" aria-label={t("تسجيل الخروج")} className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur border border-white/15 flex items-center justify-center"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="mt-6"><div className="text-white/80 text-sm">{t("مرحباً")}</div><div className="text-3xl font-black truncate" data-testid="driver-name">{d.full_name}</div>
          <div className="text-xs text-white/70 mt-1 ltr font-bold" data-testid="driver-serial">{d.serial}</div></div>
        <div className="mt-4 rounded-3xl bg-brand-light/70 backdrop-blur-md border border-white/10 p-4 flex items-center justify-between gap-3" data-testid="driver-vehicle-card">
          {v ? <div className="min-w-0"><KuwaitPlate adm={v.admin_number} plate={v.plate} /></div> : <span className="text-white/80">{t("لا توجد مركبة مرتبطة")}</span>}
          <div className="flex flex-col items-center gap-1 shrink-0"><Truck className="w-11 h-11" /><b>{(d.work_types || []).map((w) => t(SERVICE[w])).join(" + ")}</b><span className="text-xs text-white/60">{t("مركبتي")}</span></div>
        </div>
        <Link to="/driver/trips/new" data-testid="btn-new-trip" className="mt-4 rounded-2xl p-4 flex items-center justify-between bg-white text-brand shadow-xl active:scale-[0.98] transition-transform">
          <div><div className="text-2xl font-black">{t("نقلة جديدة")}</div><div className="text-xs font-bold text-slate-500">{t("اضغط هنا لبدء نقلة")}</div></div>
          <span className="w-14 h-14 rounded-full bg-brand text-white flex items-center justify-center"><Plus className="w-8 h-8" strokeWidth={3} /></span>
        </Link>
      </div>
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 390 40" preserveAspectRatio="none" style={{ height: 36 }}><path d="M0,20 C100,45 290,0 390,22 L390,40 L0,40 Z" fill="#F2F6FB" /></svg>
    </div>
  );
}

export default function DriverHome() {
  const { user, logout } = useAuth();
  const d = user.driver || {};
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/my/summary").then((r) => setS(r.data)).catch(() => {}); }, []);
  const n = (k) => (s ? s[k] : "…");
  return (
    <div className="bg-[#F2F6FB] min-h-screen">
      <Hero d={d} unread={s?.unread} logout={logout} />
      <div className="px-3 -mt-2 relative space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <StatBox to="/driver/trips" testid="driver-stat-month" bg="bg-amber-50" icon={CalendarDays} ic="text-amber-500" label={t("نقلات الشهر")} value={n("trips_month")} />
          <StatBox to="/driver/trips" testid="driver-stat-today" bg="bg-blue-50" icon={Truck} ic="text-blue-600" label={t("نقلات اليوم")} value={n("trips_today")} />
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Tile to="/driver/trips" testid="driver-tile-trips" icon={Truck} bg="bg-blue-700" title={t("نقلاتي")} sub={s ? t("{n} قيد التنفيذ", { n: s.active_trips }) : "…"} />
          <Tile to="/driver/notifications" testid="driver-tile-notifications" icon={Bell} bg="bg-red-500" title={t("إشعاراتي")} sub={t("المخالفات والتنبيهات")} badge={s?.unread} />
          <Tile to="/driver/documents" testid="driver-tile-documents" icon={FileCheck} bg="bg-emerald-600" title={t("مستنداتي")} sub={s ? t("{n} مستند", { n: s.documents }) : "…"} />
          <Tile to="/driver/account" testid="driver-tile-account" icon={Wallet} bg="bg-orange-500" title={t("حسابي")} sub={t("بياناتي والإعدادات")} />
        </div>
        <LocationNotice />
        {isIOS() && !isStandalone() && (
          <div className="card p-4 flex gap-3 items-center" data-testid="ios-install-hint">
            <Smartphone className="w-7 h-7 text-brand shrink-0" />
            <p className="text-sm text-slate-700">{t("لتثبيت التطبيق: اضغط زر المشاركة في سفاري ثم «إضافة إلى الشاشة الرئيسية».")}</p>
          </div>
        )}
        <LatestAlerts />
      </div>
    </div>
  );
}
